// Captures UTM / referral parameters on first landing and persists them
// for the lifetime of the browser session. Idempotent — call from app root.
// Stored in localStorage so they survive auth redirects and tab reloads.

const KEY = "savey:attribution";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_path?: string;
  first_seen_at: string;
}

export function captureAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const existing = window.localStorage.getItem(KEY);
    if (existing) return JSON.parse(existing) as Attribution;

    const params = new URLSearchParams(window.location.search);
    const next: Attribution = { first_seen_at: new Date().toISOString() };

    let hasAny = false;
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) {
        (next as any)[k] = v.slice(0, 120);
        hasAny = true;
      }
    }

    // Capture document.referrer when it's from a different host (real traffic source).
    const ref = document.referrer;
    if (ref) {
      try {
        const refHost = new URL(ref).host;
        if (refHost && refHost !== window.location.host) {
          next.referrer = ref.slice(0, 200);
          hasAny = true;
        }
      } catch {
        // ignore malformed referrer
      }
    }

    if (!hasAny) return null;

    next.landing_path = window.location.pathname.slice(0, 120);
    window.localStorage.setItem(KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}

export function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}
